import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Wallet, 
  Receipt, 
  History, 
  Download, 
  Eye,
  Calendar,
  CreditCard,
  TrendingDown,
  FileText
} from 'lucide-react';
import InvoiceReceipt from './InvoiceReceipt';

const PlayerWallet = ({ playerId, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  // Mock transaction data (in real app, fetch from backend)
  useEffect(() => {
    const mockTransactions = [
      {
        id: 'txn_001',
        invoiceNumber: 'PLAY-2025-01-15-0001',
        date: new Date('2025-01-15T10:30:00'),
        gameName: 'Lightning Quiz',
        amount: 10,
        status: 'completed',
        paymentMode: 'UPI',
        gstAmount: 1.53,
        taxableAmount: 8.47
      },
      {
        id: 'txn_002',
        invoiceNumber: 'PLAY-2025-01-14-0087',
        date: new Date('2025-01-14T15:45:00'),
        gameName: 'Tap Challenge',
        amount: 10,
        status: 'completed',
        paymentMode: 'Paytm',
        gstAmount: 1.53,
        taxableAmount: 8.47
      },
      {
        id: 'txn_003',
        invoiceNumber: 'PLAY-2025-01-13-0156',
        date: new Date('2025-01-13T20:15:00'),
        gameName: 'Memory Master',
        amount: 10,
        status: 'completed',
        paymentMode: 'Razorpay',
        gstAmount: 1.53,
        taxableAmount: 8.47
      }
    ];
    setTransactions(mockTransactions);
  }, [playerId]);

  const totalSpent = transactions.reduce((sum, txn) => sum + txn.amount, 0);
  const totalTax = transactions.reduce((sum, txn) => sum + txn.gstAmount, 0);

  const handleViewInvoice = (transaction) => {
    setSelectedInvoice({
      amount: transaction.amount,
      gameName: transaction.gameName,
      invoiceNumber: transaction.invoiceNumber,
      date: transaction.date,
      paymentMode: transaction.paymentMode,
      playerName: 'Current Player' // In real app, get from player data
    });
    setShowInvoice(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-40 p-4">
        <Card className="gaming-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <CardHeader className="pb-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl text-white flex items-center space-x-2">
                <Wallet className="w-6 h-6 text-yellow-400" />
                <span>Payment History & Receipts</span>
              </CardTitle>
              <Button onClick={onClose} className="btn-secondary px-3 py-1">
                ✕
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Wallet Summary */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-blue-400 flex items-center space-x-2">
                    <CreditCard className="w-5 h-5" />
                    <span>Total Spent</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">₹{totalSpent}</div>
                  <div className="text-blue-400 text-sm">Across {transactions.length} games</div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-500/30">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-green-400 flex items-center space-x-2">
                    <TrendingDown className="w-5 h-5" />
                    <span>GST Paid</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">₹{totalTax.toFixed(2)}</div>
                  <div className="text-green-400 text-sm">Tax component @ 18%</div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border-purple-500/30">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-purple-400 flex items-center space-x-2">
                    <Receipt className="w-5 h-5" />
                    <span>Invoices</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{transactions.length}</div>
                  <div className="text-purple-400 text-sm">Available receipts</div>
                </CardContent>
              </Card>
            </div>

            {/* Transaction History */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <History className="w-5 h-5 text-yellow-400" />
                <span>Transaction History</span>
              </h3>

              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <div className="text-gray-400 text-lg">No transactions yet</div>
                  <div className="text-gray-500 text-sm">Play games to see your payment history</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.map((transaction, index) => (
                    <Card key={transaction.id} className="bg-gray-800/50 border-gray-700 hover:border-yellow-400/50 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="p-3 bg-yellow-400/20 rounded-full">
                              <Receipt className="w-6 h-6 text-yellow-400" />
                            </div>
                            <div>
                              <div className="font-semibold text-white text-lg">{transaction.gameName}</div>
                              <div className="text-gray-400 text-sm">
                                {transaction.date.toLocaleDateString('en-IN')} at {transaction.date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <div className="text-gray-500 text-xs mt-1">
                                Invoice: {transaction.invoiceNumber} | {transaction.paymentMode}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-white">₹{transaction.amount}</div>
                              <Badge className={`${
                                transaction.status === 'completed' 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                              }`}>
                                {transaction.status === 'completed' ? '✅ Paid' : '⏳ Pending'}
                              </Badge>
                            </div>

                            <div className="flex flex-col gap-2">
                              <Button 
                                onClick={() => handleViewInvoice(transaction)}
                                className="btn-primary px-4 py-2 text-sm flex items-center space-x-2"
                              >
                                <Eye className="w-4 h-4" />
                                <span>View Receipt</span>
                              </Button>
                              <Button 
                                onClick={() => {
                                  // Download individual receipt
                                  console.log('Download receipt:', transaction.invoiceNumber);
                                }}
                                className="btn-secondary px-4 py-2 text-sm flex items-center space-x-2"
                              >
                                <Download className="w-4 h-4" />
                                <span>Download</span>
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Tax Breakdown */}
                        <div className="mt-4 pt-4 border-t border-gray-700">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div className="text-center">
                              <div className="text-gray-400">Taxable Amount</div>
                              <div className="text-white font-semibold">₹{transaction.taxableAmount.toFixed(2)}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-400">GST (18%)</div>
                              <div className="text-white font-semibold">₹{transaction.gstAmount.toFixed(2)}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-400">Total Paid</div>
                              <div className="text-yellow-400 font-semibold">₹{transaction.amount.toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Download All Receipts */}
            {transactions.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-700">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button className="btn-secondary flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Download All Receipts (ZIP)</span>
                  </Button>
                  <Button className="btn-secondary flex items-center space-x-2">
                    <FileText className="w-4 h-4" />
                    <span>Generate Tax Summary</span>
                  </Button>
                </div>
              </div>
            )}

            {/* GST Information */}
            <div className="mt-8 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <h4 className="text-lg font-semibold text-blue-400 mb-3">GST Information</h4>
              <div className="text-gray-300 text-sm space-y-2">
                <p>• All payments include 18% GST as per Indian gaming tax regulations</p>
                <p>• Invoices are issued by PLAYEDGE INNOVATIONS (GST Registered)</p>
                <p>• Keep these receipts for your tax records and accounting purposes</p>
                <p>• For tax queries, contact our support team</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Modal */}
      {showInvoice && selectedInvoice && (
        <InvoiceReceipt 
          paymentData={selectedInvoice}
          onClose={() => setShowInvoice(false)}
          onDownload={(invoiceNum) => {
            console.log('Downloaded invoice:', invoiceNum);
            setShowInvoice(false);
          }}
        />
      )}
    </>
  );
};

export default PlayerWallet;